import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MapIcon from "@mui/icons-material/Map";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import LoadingSplash from "@/components/LoadingSplash";
import { STATUS_COLORS } from "@/lib/statusColors";


type MapNote = {
  id: number;
  lat: number;
  lng: number;
  accuracy: number;
  location_at: string;
  location_method: string;
  status: string;
  created_at: string;
  address_display: string;
  address_snippet: string;
  udprn: string;
  followup_date: string | null;
};

type MapStats = {
  total: number;
  by_status: Record<string, number>;
  followups: number;
};

type MapBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

type MapResponse = {
  date: string;
  user: {
    id: number;
    name: string;
  };
  notes: MapNote[];
  stats: MapStats;
  map?: {
    center?: { lat: number; lng: number };
    bounds?: MapBounds;
  };
};

const defaultCenter = { lat: 53.7996, lng: -1.5491 }; // Leeds
const defaultZoom = 13;
const MAP_HEIGHT = 360;

const statusIconUrls: Record<string, string> = {
  INTERESTED: "https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png",
  CALLBACK: "https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png",
  OTHER: "https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png",
  QUOTED: "https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png",
  LEAFLET: "https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png",
  OBSERVED_DIRTY: "https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png",
  REGULAR: "https://maps.gstatic.com/mapfiles/ms2/micons/green-dot.png",
  CUSTOMER: "https://maps.gstatic.com/mapfiles/ms2/micons/purple-dot.png",
  NOT_INTERESTED: "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
  EXCLUDE_LOW_DEM: "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
  EXCLUDE_ACCESS: "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
  default: "https://maps.gstatic.com/mapfiles/ms2/micons/blue-dot.png",
};

const formatDateInput = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const addDays = (isoDate: string, delta: number) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return formatDateInput(d);
};

async function fetchMapNotes(date: string, userId: number): Promise<MapResponse> {
  const res = await api.get("/admin/map-notes/", {
    params: { user_id: userId, date },
  });
  return res.data;
}

type MapUser = { id: number; name: string };

async function fetchUsers(): Promise<MapUser[]> {
  const res = await api.get("/admin/users/");
  return res.data;
}

export default function NoteMap() {
  const { user, loading: authLoading } = useAuth();
  const today = useMemo(() => formatDateInput(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<number | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapDebug, setMapDebug] = useState<{ attached: boolean; center?: google.maps.LatLngLiteral; zoom?: number }>({
    attached: false,
  });
  const [mapReady, setMapReady] = useState(false);

  const usersQuery = useQuery({
    queryKey: ["note-map-users"],
    queryFn: fetchUsers,
    staleTime: 5 * 60_000,
  });

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ["note-map", selectedDate, selectedUserId],
    queryFn: () => fetchMapNotes(selectedDate, selectedUserId as number),
    staleTime: 30_000,
    enabled: selectedUserId !== null,
  });

  const notes = data?.notes ?? [];
  const stats = data?.stats;
  const mapsApiKey = user?.google_maps_api_key || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const mapsKeyMissing = !mapsApiKey;
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
    id: "note-map-google",
    googleMapsApiKey: mapsApiKey || "missing-key",
    preventGoogleFontsLoading: true,
  });

  useEffect(() => {
    if (!mapsLoaded || !mapReady || !mapRef.current) return;

    if (notes.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      notes.forEach((note) => {
        bounds.extend({ lat: note.lat, lng: note.lng });
      });
      mapRef.current.fitBounds(bounds, 64);
      return;
    }

    if (data?.map?.bounds) {
      const b = data.map.bounds;
      const bounds = new window.google.maps.LatLngBounds(
        { lat: b.south, lng: b.west },
        { lat: b.north, lng: b.east }
      );
      mapRef.current.fitBounds(bounds, 64);
      return;
    }

    mapRef.current.setCenter(defaultCenter);
    mapRef.current.setZoom(defaultZoom);
  }, [mapsLoaded, mapReady, notes, data?.map]);

  useEffect(() => {
    setActiveNoteId(null);
  }, [selectedDate, selectedUserId]);

  if (authLoading) return <LoadingSplash />;
  if (!user?.is_superuser) return <Navigate to="/search" replace />;

  const canGoForward = selectedDate < today;
  const handlePrev = () => setSelectedDate((d) => addDays(d, -1));
  const handleNext = () => {
    if (canGoForward) setSelectedDate((d) => addDays(d, 1));
  };
  const handleUserChange = (id: number | null) => {
    setSelectedUserId(id);
    setActiveNoteId(null);
  };

  const renderMap = () => {
    if (mapsKeyMissing) {
      return (
        <Alert severity="warning">
          Google Maps API key missing. Add one to /me or env to view the map.
        </Alert>
      );
    }

    if (mapsLoadError) {
      return (
        <Alert severity="error">
          Google Maps failed to load: {String(mapsLoadError.message || mapsLoadError)}
        </Alert>
      );
    }

    if (!mapsLoaded) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 360 }}>
          <CircularProgress />
        </Box>
      );
    }

    const initialCenter = data?.map?.center ?? defaultCenter;

    

    return (
      
      <Box
        sx={{
          position: "relative",
          height: MAP_HEIGHT,
          minHeight: MAP_HEIGHT,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#f5f5f5",
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: `${MAP_HEIGHT}px` }}
          center={initialCenter}
          zoom={defaultZoom}
          onLoad={(map) => {
            mapRef.current = map;
            setMapReady(true);
            setMapDebug({
              attached: true,
              center: { lat: map.getCenter()?.lat() ?? 0, lng: map.getCenter()?.lng() ?? 0 },
              zoom: map.getZoom() ?? undefined,
            });
            map.addListener("idle", () => {
              setMapDebug({
                attached: true,
                center: { lat: map.getCenter()?.lat() ?? 0, lng: map.getCenter()?.lng() ?? 0 },
                zoom: map.getZoom() ?? undefined,
              });
            });
          }}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {notes.map((note) => (
            <Marker
              key={note.id}
              position={{ lat: note.lat, lng: note.lng }}
              onClick={() => setActiveNoteId(note.id)}
              icon={{ url: statusIconUrls[note.status] ?? statusIconUrls.default }}
            />
          ))}

          {notes.map(
            (note) =>
              activeNoteId === note.id && (
                <InfoWindow
                  key={`info-${note.id}`}
                  position={{ lat: note.lat, lng: note.lng }}
                  onCloseClick={() => setActiveNoteId(null)}
                >
                  <Box sx={{ maxWidth: 240 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {note.address_display.trim() || "Address"}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip
                        label={note.status}
                        size="small"
                        sx={{
                          bgcolor: STATUS_COLORS[note.status] ?? "grey.700",
                          color: "white",
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(note.location_at).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Accuracy: {Math.round(note.accuracy)}m · Method: {note.location_method}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        component={Link}
                        to={`/address/${note.udprn}`}
                        sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}
                      >
                        Open address
                      </Typography>
                    </Box>
                  </Box>
                </InfoWindow>
              )
          )}
        </GoogleMap>

        {(isPending || isFetching) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.4)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ px: 2, pb: 3 }}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select user
        </Typography>
        <Select
          fullWidth
          displayEmpty
          value={selectedUserId !== null ? String(selectedUserId) : ""}
          onChange={(e) => handleUserChange(e.target.value === "" ? null : Number(e.target.value))}
          renderValue={(v) => {
            const val = String(v);
            if (val === "") return "Choose a user";
            const match = usersQuery.data?.find((u) => u.id === Number(val));
            return match?.name ?? "Choose a user";
          }}
        >
          <MenuItem value="">Choose a user</MenuItem>
          {usersQuery.data?.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.name} (#{u.id})
            </MenuItem>
          ))}
        </Select>
        {usersQuery.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Failed to load users
          </Alert>
        )}
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <IconButton onClick={handlePrev} aria-label="Previous day">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="subtitle2" color="text.secondary">
              Note Map
            </Typography>
            <Typography variant="h6">{formatDateLabel(selectedDate)}</Typography>
          </Stack>
          <IconButton onClick={handleNext} aria-label="Next day" disabled={!canGoForward}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tap the date later to jump via calendar. Select a user to load data.
        </Typography>
      </Paper>

      {selectedUserId === null ? (
        <Alert severity="info">Choose a user to load map and stats.</Alert>
      ) : (
        <Paper elevation={1} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <MapIcon fontSize="small" color="action" />
          <Typography variant="subtitle1">Map</Typography>
          <Chip label={`${notes.length} notes`} size="small" />
        </Stack>

        {isError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton size="small" color="inherit" onClick={() => refetch()} aria-label="Retry">
                <RefreshIcon fontSize="small" />
              </IconButton>
            }
          >
            Failed to load notes: {(error as Error)?.message ?? "Unknown error"}
          </Alert>
        )}

        {notes.length === 0 && !isPending && !isFetching && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No notes for this date. Showing default map view.
          </Alert>
        )}

        {renderMap()}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Debug — loaded: {String(mapsLoaded)}; loadError: {mapsLoadError ? String(mapsLoadError) : "none"}; key present:{" "}
          {mapsKeyMissing ? "no" : "yes"}; notes: {notes.length}; map attached: {String(mapDebug.attached)}; center:{" "}
          {mapDebug.center ? `${mapDebug.center.lat.toFixed(6)}, ${mapDebug.center.lng.toFixed(6)}` : "n/a"}; zoom:{" "}
          {mapDebug.zoom ?? "n/a"}
        </Typography>
        </Paper>
      )}

      {selectedUserId !== null && (
        <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Stats
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`Total: ${stats?.total ?? 0}`} />
            <Chip label={`Follow-ups: ${stats?.followups ?? 0}`} />
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={1}>
            {stats && Object.keys(stats.by_status).length > 0 ? (
              Object.entries(stats.by_status).map(([status, count]) => (
                <Stack key={status} direction="row" alignItems="center" spacing={1}>
                  <Chip
                    size="small"
                    label={status}
                    sx={{
                      bgcolor: STATUS_COLORS[status] ?? "grey.200",
                      color: STATUS_COLORS[status] ? "white" : "text.primary",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {count}
                  </Typography>
                </Stack>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No status breakdown available.
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
